import { pickPreferredDevice } from '@open-songster/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { readString, removeKey, writeString } from '../lib/storage'
import type { SpotifyDevice, SpotifyProfile } from '../services/spotify-api'
import * as spotify from '../services/spotify-api'
import { spotifyAuthClient } from '../services/spotify-auth'
import type { BrowserPlayerHandle } from '../services/spotify-playback'
import { createBrowserPlayer } from '../services/spotify-playback'

const DEVICE_KEY = 'songster.spotify.deviceId'
const DEVICE_MODE_KEY = 'songster.spotify.deviceMode'
const BROWSER_DEVICE_NAME = 'Open Songster (this browser)'

export const useSpotifyStore = defineStore('spotify', () => {
  const connected = ref(spotifyAuthClient.isConnected())
  const profile = ref<SpotifyProfile | null>(null)
  const devices = ref<SpotifyDevice[]>([])
  const chosenDeviceId = ref<string | null>(readString(DEVICE_KEY))
  /**
   * Automatic (the default) sends play commands without a device: Spotify
   * routes them to whatever the host picked in the Spotify app. That covers
   * speakers the Web API never lists — Sonos among them.
   */
  const automaticDevice = ref(readString(DEVICE_MODE_KEY) !== 'device')
  /** The device play commands are addressed to; `null` in automatic mode. */
  const selectedDeviceId = computed(() => (automaticDevice.value ? null : chosenDeviceId.value))
  const browserDeviceId = ref<string | null>(null)
  const error = ref<string | null>(null)
  const busy = ref(false)

  let browserPlayer: BrowserPlayerHandle | null = null

  const isPremium = computed(() => (profile.value ? spotify.isPremium(profile.value) : false))
  const needsPremium = computed(() => profile.value !== null && !isPremium.value)
  const selectedDevice = computed(
    () => devices.value.find((device) => device.id === chosenDeviceId.value) ?? null,
  )

  async function connect(): Promise<void> {
    window.location.assign(await spotifyAuthClient.beginLogin(window.location.pathname))
  }

  async function completeConnect(search: string): Promise<string | null> {
    await spotifyAuthClient.completeLogin(search)
    connected.value = true
    await loadProfile()
    return spotifyAuthClient.takeReturnTo()
  }

  function disconnect(): void {
    browserPlayer?.disconnect()
    browserPlayer = null
    browserDeviceId.value = null
    spotifyAuthClient.logout()
    removeKey(DEVICE_KEY)
    removeKey(DEVICE_MODE_KEY)
    connected.value = false
    profile.value = null
    devices.value = []
    chosenDeviceId.value = null
    automaticDevice.value = true
  }

  async function loadProfile(): Promise<void> {
    if (!connected.value) return
    profile.value = await spotify.getProfile()
  }

  async function refreshDevices(): Promise<void> {
    if (!connected.value) return
    busy.value = true
    try {
      devices.value = await spotify.getDevices()
      if (!selectedDevice.value) {
        chosenDeviceId.value = pickPreferredDevice(devices.value, browserDeviceId.value)?.id ?? null
      }
      if (chosenDeviceId.value) writeString(DEVICE_KEY, chosenDeviceId.value)
    } finally {
      busy.value = false
    }
  }

  function selectDevice(deviceId: string): void {
    chosenDeviceId.value = deviceId
    automaticDevice.value = false
    writeString(DEVICE_KEY, deviceId)
    writeString(DEVICE_MODE_KEY, 'device')
  }

  function useAutomaticDevice(): void {
    automaticDevice.value = true
    writeString(DEVICE_MODE_KEY, 'auto')
  }

  /**
   * Registers this browser as a Connect device without starting audio. Safe
   * to call anywhere (no gesture needed) and idempotent, so the game screen
   * can do it on mount and have the device ready by the first tap.
   */
  async function connectBrowserPlayer(): Promise<void> {
    if (browserPlayer) return
    browserPlayer = await createBrowserPlayer(BROWSER_DEVICE_NAME, {
      onError: (_kind, message) => {
        error.value = message
      },
    })
    browserDeviceId.value = browserPlayer.deviceId
    await refreshDevices().catch(() => undefined)
  }

  /**
   * Unlocks audio for the in-browser device. Browsers, iOS Safari above all,
   * only honour this while a user gesture is still "live", which ends at the
   * first `await` — so callers must invoke it synchronously from the tap
   * handler, before anything else. A player that is not connected yet is
   * connected first; that activation may be too late for iOS, but the next
   * tap catches up.
   */
  function activateBrowserPlayback(): Promise<void> {
    if (browserPlayer) return browserPlayer.activate()
    return connectBrowserPlayer().then(() => browserPlayer?.activate())
  }

  /** Picker button: register this browser and make it the selected device. */
  async function enableBrowserPlayback(): Promise<void> {
    await activateBrowserPlayback()
    if (browserDeviceId.value) selectDevice(browserDeviceId.value)
  }

  /** Whether playing needs our own SDK device: explicitly chosen, or nothing else to choose. */
  const usesBrowserDevice = computed(
    () =>
      !automaticDevice.value &&
      (chosenDeviceId.value === null ||
        (browserDeviceId.value !== null && chosenDeviceId.value === browserDeviceId.value)),
  )

  return {
    connected,
    profile,
    devices,
    automaticDevice,
    chosenDeviceId,
    selectedDeviceId,
    selectedDevice,
    browserDeviceId,
    error,
    busy,
    isPremium,
    needsPremium,
    usesBrowserDevice,
    connect,
    completeConnect,
    disconnect,
    loadProfile,
    refreshDevices,
    selectDevice,
    useAutomaticDevice,
    connectBrowserPlayer,
    activateBrowserPlayback,
    enableBrowserPlayback,
  }
})
